import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AUTH_CONFIG } from '../config';
import { AUTH_EXPIRED, AUTH_STORAGE_KEY, advanceSessionRevision, notifyAuthTabs, sessionRevision } from '../auth/sessionEvents';

const AuthContext = createContext(null);

/** One cookie-backed session for the whole page. Wiki admin JWTs are independent. */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const currentUser = useRef(null);
  const generation = useRef(0);
  const pending = useRef(null);
  const alive = useRef(false);
  const loggingOut = useRef(false);

  const cancelCheck = useCallback(() => {
    generation.current++;
    pending.current?.controller.abort();
    pending.current = null;
  }, []);

  const invalidate = useCallback((broadcast = true) => {
    cancelCheck();
    advanceSessionRevision();
    currentUser.current = null;
    setUser(null); setLoading(false); setError(null);
    if (broadcast) notifyAuthTabs('signed-out');
  }, [cancelCheck]);

  const refresh = useCallback(() => {
    if (loggingOut.current) return Promise.resolve(null);
    if (pending.current) return pending.current.promise;
    const controller = new AbortController();
    const requestGeneration = ++generation.current;
    const timeout = setTimeout(() => controller.abort(), 10000);
    const promise = (async () => {
      try {
        const response = await fetch(`${AUTH_CONFIG.apiUrl}/me`, {
          credentials: 'include', cache: 'no-store', signal: controller.signal,
          headers: { Accept: 'application/json' },
        });
        if (!alive.current || requestGeneration !== generation.current) return null;
        if (response.status === 401) { invalidate(); return null; }
        if (!response.ok) throw new Error('Проверка входа временно недоступна.');
        const data = await response.json();
        if (!alive.current || requestGeneration !== generation.current) return null;
        if (data?.authenticated === false) { invalidate(); return null; }
        if (!data?.authenticated || !data?.user) throw new Error('Не удалось проверить вход.');
        // Keep object identity stable on heartbeat, avoiding needless private-data reloads.
        if (JSON.stringify(currentUser.current) !== JSON.stringify(data.user)) {
          advanceSessionRevision();
          currentUser.current = data.user;
          setUser(data.user);
          notifyAuthTabs('changed');
        }
        setError(null);
        return data.user;
      } catch (_) {
        if (alive.current && requestGeneration === generation.current) {
          // Network failures do not prove that a previously verified session expired.
          setError('Не удалось проверить вход. Проверь соединение и попробуй ещё раз.');
        }
        return null;
      } finally {
        clearTimeout(timeout);
        if (requestGeneration === generation.current) {
          pending.current = null;
          if (alive.current) setLoading(false);
        }
      }
    })();
    pending.current = { controller, promise };
    return promise;
  }, [invalidate]);

  useEffect(() => {
    alive.current = true;
    const visible = () => { if (document.visibilityState !== 'hidden') refresh(); };
    const expired = event => { if (event.detail === sessionRevision()) invalidate(); };
    const storage = event => {
      if (event.key !== AUTH_STORAGE_KEY || !event.newValue) return;
      try {
        const message = JSON.parse(event.newValue);
        if (message.type === 'signed-out') invalidate(false);
        if (message.type === 'changed') { cancelCheck(); refresh(); }
      } catch (_) {}
    };
    window.addEventListener(AUTH_EXPIRED, expired);
    window.addEventListener('storage', storage);
    window.addEventListener('focus', visible);
    window.addEventListener('pageshow', visible);
    document.addEventListener('visibilitychange', visible);
    const timer = setInterval(visible, 60000);
    refresh();
    return () => {
      alive.current = false; cancelCheck(); clearInterval(timer);
      window.removeEventListener(AUTH_EXPIRED, expired);
      window.removeEventListener('storage', storage);
      window.removeEventListener('focus', visible);
      window.removeEventListener('pageshow', visible);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [refresh, invalidate, cancelCheck]);

  const login = useCallback(() => { window.location.href = `${AUTH_CONFIG.apiUrl}/login`; }, []);
  const logout = useCallback(async () => {
    if (loggingOut.current) return false;
    loggingOut.current = true;
    cancelCheck();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(`${AUTH_CONFIG.apiUrl}/logout`, {
        method: 'POST', credentials: 'include', signal: controller.signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok && response.status !== 401) throw new Error();
      if (alive.current) invalidate();
      return true;
    } catch (_) {
      if (alive.current) setError('Не удалось выйти. Проверь соединение и попробуй ещё раз.');
      return false;
    } finally { clearTimeout(timer); loggingOut.current = false; }
  }, [cancelCheck, invalidate]);

  const devLogin = useCallback(async username => {
    try {
      const response = await fetch(`${AUTH_CONFIG.apiUrl}/dev-login`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ username }),
      });
      if (!response.ok) throw new Error('Тестовый вход недоступен');
      cancelCheck();
      const loggedIn = await refresh();
      return loggedIn ? { success: true } : { success: false, message: 'Не удалось проверить вход.' };
    } catch (e) { return { success: false, message: e.message || 'Ошибка тестового входа' }; }
  }, [cancelCheck, refresh]);

  const value = useMemo(() => ({ user, loading, error, login, logout, refresh, devLogin }), [user, loading, error, login, logout, refresh, devLogin]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const auth = useContext(AuthContext);
  if (!auth) throw new Error('useAuth requires AuthProvider');
  return auth;
}
